import React, { useEffect } from "react";
import {
    Dialog,
    Grid,
    Box,
    Typography,
    IconButton,
    useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useForm } from "react-hook-form";
import { Close, EditOutlined } from "@mui/icons-material";
import { Pill } from "lucide-react";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import CustomTextField from "../../common/CustomTextField";
import { useModalStyles } from "../../common/useModalStyles";
import { FieldLabel } from "../../../utils/Utility";

export interface PracticePharmacyFormData {
    manufacturerId: string;
    billingCode: string;
}

export interface PracticePharmacyRow {
    manufacturerId: number;
    manufacturerName: string;
    billingCode: string;
}

interface PracticePharmacyPopupProps {
    open: boolean;
    onClose: () => void;
    /** Pharmacies still selectable — the ones already configured are excluded upstream. */
    pharmacyOptions: { label: string; value: number }[];
    onSave: (row: PracticePharmacyRow) => void;
    editData?: PracticePharmacyRow;
    isEditMode?: boolean;
}

/**
 * CS-2075 — Add / Edit a practice's pharmacy billing code.
 * Validates locally and hands the row back through `onSave`; the practice form
 * persists it via savePracticePharmacyConfig, independently of the practice Save.
 */
const PracticePharmacyPopup: React.FC<PracticePharmacyPopupProps> = ({
    open,
    onClose,
    pharmacyOptions,
    onSave,
    editData,
    isEditMode = false,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const {
        tokens,
        paperSx,
        headerSx,
        iconBoxSx,
        closeIconSx,
        bodySx,
        footerSx,
        fieldWrapSx,
        primaryBtnSx,
        cancelBtnSx,
    } = useModalStyles();

    const { control, handleSubmit, reset, formState: { errors } } =
        useForm<PracticePharmacyFormData>({
            defaultValues: { manufacturerId: "", billingCode: "" },
        });

    useEffect(() => {
        if (!open) return;
        reset({
            manufacturerId: editData ? String(editData.manufacturerId) : "",
            billingCode: editData?.billingCode ?? "",
        });
    }, [open, editData, reset]);

    // Values are seeded by the `open` effect, so they are NOT cleared here — resetting
    // on close repaints the fields mid close-animation and shows as a flicker.
    const handleClose = () => {
        onClose();
    };

    const onSubmit = (data: PracticePharmacyFormData) => {
        const manufacturerId = Number(data.manufacturerId);
        const selected = pharmacyOptions.find(
            (option) => Number(option.value) === manufacturerId
        );
        onSave({
            manufacturerId,
            manufacturerName: selected?.label ?? editData?.manufacturerName ?? "",
            billingCode: (data.billingCode ?? "").trim(),
        });
        handleClose();
    };

    return (
        <Dialog
            open={open}
            keepMounted
            fullWidth
            onClose={handleClose}
            maxWidth="sm"
            fullScreen={isMobile}
            PaperProps={{ sx: paperSx(isMobile) }}
            onClick={(event) => {
                event.stopPropagation();
            }}
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <Box sx={headerSx}>
                    <Box sx={iconBoxSx(tokens.accent)}>
                        <Pill size={20} color={tokens.accent} strokeWidth={1.8} />
                    </Box>
                    <Box sx={{ flex: 1, pr: 4 }}>
                        <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}>
                            {isEditMode ? "Edit Pharmacy" : "Add Pharmacy"}
                        </Typography>
                        <Typography sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
                            {isEditMode
                                ? "Update the billing code this pharmacy assigned to the practice."
                                : "Select the pharmacy and enter the billing code it assigned to this practice."}
                        </Typography>
                    </Box>
                    <IconButton onClick={handleClose} size="small" sx={closeIconSx} aria-label="Close">
                        <Close sx={{ fontSize: "1rem" }} />
                    </IconButton>
                </Box>

                <Box sx={bodySx}>
                    <Grid container spacing={2}>
                        <Grid item md={6} xs={12}>
                            <Box>
                                <FieldLabel label="Pharmacy" required tokens={tokens} />
                                <Box sx={fieldWrapSx(!!errors?.manufacturerId)}>
                                    <CustomDropdownField
                                        name="manufacturerId"
                                        control={control}
                                        options={pharmacyOptions}
                                        label=""
                                        disabled={isEditMode}
                                        rules={{ required: "Pharmacy is required." }}
                                    />
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item md={6} xs={12}>
                            <Box>
                                <FieldLabel label="Billing Code" required tokens={tokens} />
                                <Box sx={fieldWrapSx(!!errors?.billingCode)}>
                                    <CustomTextField
                                        control={control}
                                        controllerName="billingCode"
                                        label=""
                                        rules={{
                                            required: "Billing Code is required.",
                                            validate: (value: any) =>
                                                String(value ?? "").trim() !== "" ||
                                                "Billing Code is required.",
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>

                    <Box
                        sx={{
                            ...footerSx,
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 1.5,
                            flexDirection: { xs: "column", sm: "row" },
                        }}
                    >
                        <Box
                            component="button"
                            type="submit"
                            sx={{
                                ...primaryBtnSx,
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 0.8,
                                px: 3,
                                py: 1.2,
                                border: "none",
                                cursor: "pointer",
                                color: "#fff",
                                order: { xs: 1, sm: 1 },
                            }}
                        >
                            <EditOutlined sx={{ fontSize: "1.1rem" }} />
                            {isEditMode ? "Update" : "Add"}
                        </Box>
                        <Box
                            component="button"
                            type="button"
                            onClick={handleClose}
                            sx={{
                                ...cancelBtnSx,
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 0.8,
                                px: 3,
                                py: 1.2,
                                border: "1px solid",
                                cursor: "pointer",
                                order: { xs: 2, sm: 2 },
                            }}
                        >
                            Cancel
                        </Box>
                    </Box>
                </Box>
            </form>
        </Dialog>
    );
};

export default PracticePharmacyPopup;
